import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import MarketStateFilterInput from '@app/model/market_state/input/MarketStateFilterInput';
import CompanyHotelDashboardFilter from '@app/model/company_hotel/settings/CompanyHotelDashboardFilter';
import DataProvider from '@app/model/_common/DataProvider';
import DataPlatform from '@app/model/_common/DataPlatform';
import Region from '@app/model/market_state/Region';
import RatesType from '@app/model/market_state/RatesType';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import Refunding from '@app/model/market_state/Refunding';
import { usePrevious } from 'react-use';
import { useQuery } from '@tanstack/react-query';
import {
	SavedMarketStateFilterService,
} from '@app/model/market_state/_saved_filters/service/SavedMarketStateFilterService';
import {
	SavedMarketStateFilterGraphQLQueryKeys,
} from '@app/model/market_state/_saved_filters/service/SavedMarketStateFilterGraphQLQueryKeys';
import { AirportCodeJet2Key, AirportCodeTuiUk, AirportCodeTuiUkKey } from '@app/model/market_state/AirportCode';

export type FilterConfig = {
	enabled: boolean;
	defaultValue?: any;
};

const defaultFilters: MarketStateFilterInput = {
	roomTypes: [],
	provider: DataProvider.BOOKING,
	platform: DataPlatform.WEB,
	region: Region.EUROPE,
	meal: undefined,
	refunding: Refunding.NONE,
	ratesType: RatesType.PUBLIC,
	guests: 2,
	nights: 1,
	airportCode: undefined,
	competitorTagIds: [],
};

const getParityFilters = (filters: MarketStateFilterInput, nights: number) => {
	return { ...filters, nights: nights, roomTypes: [], ratesType: RatesType.PUBLIC, meal: undefined, guests: 2 };
};

export const MarketStateFilterInputContext = React.createContext<MarketStateFilterInputTools>({
	filter: defaultFilters,
	getParityFilters: (nights: number) => getParityFilters(defaultFilters, nights),
	setFilters: () => {
	},
	resetFilters: () => {
	},
	isDashboardFilterEnabled: () => false,
});

export type MarketStateFilterInputTools = {
	filter: MarketStateFilterInput,
	getParityFilters: (nights: number) => MarketStateFilterInput,
	setFilters: (filter: MarketStateFilterInput) => void,
	resetFilters: () => void,
	isDashboardFilterEnabled: (filter: CompanyHotelDashboardFilter) => boolean
}

export const MarketStateFilterInputProvider: React.FunctionComponent<PropsWithChildren<{ companyHotel: CompanyHotel }>>
	= ({ children, companyHotel }) => {

	const { data: savedFilter, isLoading, isSuccess } = useQuery({
		queryFn: () => SavedMarketStateFilterService.findDefaultMarketStateFilter(companyHotel.id),
		queryKey: SavedMarketStateFilterGraphQLQueryKeys.default(companyHotel.id),
	});

	if (isLoading) return null;

	if (isSuccess && !!savedFilter) {
		const msFilter = {...savedFilter.filter, competitorTagIds: defaultFilters.competitorTagIds}

		return <InnerMarketStateFilterInputProvider companyHotel={companyHotel}
																								defaultFilters={msFilter}
																								children={children} />;

	} else {
		return <InnerMarketStateFilterInputProvider companyHotel={companyHotel}
																								defaultFilters={defaultFilters}
																								children={children} />;
	}

};


const InnerMarketStateFilterInputProvider: React.FunctionComponent<PropsWithChildren<{
	companyHotel: CompanyHotel,
	defaultFilters: MarketStateFilterInput
}>>
	= ({ children, companyHotel, defaultFilters }) => {
	const [filters, _setFilters] = useState<MarketStateFilterInput>(defaultFilters);
	const prevFilters = usePrevious(filters);

	useEffect(() => {
		const defaultRegion = companyHotel.settings.main.region;
		const defaultNights = companyHotel.settings.defaultParsing?.nights?.[0] ?? 1;
		setFilters({
			...defaultFilters, ...{
				region: defaultRegion,
				nights: defaultNights,
			},
		});
	}, [companyHotel.settings.main.region]);

	useEffect(() => {
		const defaultRegion = companyHotel.settings.main.region;
		let newRegion: Region | undefined = undefined;

		if (!!prevFilters && prevFilters.provider != filters.provider) {
			//when changing provider use the default values defined for the new provider
			const filterDefaultValues = findProviderFiltersConfiguration(companyHotel, prevFilters).get(filters.provider!);
			if (!!filterDefaultValues) {
				//set the default values in filters
				const newFilters: MarketStateFilterInput = {
					...filters,
					provider: filterDefaultValues.get(CompanyHotelDashboardFilter.PROVIDER)?.defaultValue,
					region: filterDefaultValues.get(CompanyHotelDashboardFilter.REGION)?.defaultValue,
					guests: filterDefaultValues.get(CompanyHotelDashboardFilter.GUESTS)?.defaultValue,
					nights: filterDefaultValues.get(CompanyHotelDashboardFilter.NIGHTS)?.defaultValue,
					ratesType: filterDefaultValues.get(CompanyHotelDashboardFilter.RATE)?.defaultValue,
					platform: filterDefaultValues.get(CompanyHotelDashboardFilter.PLATFORM)?.defaultValue,
					refunding: filterDefaultValues.get(CompanyHotelDashboardFilter.FLEX)?.defaultValue,
					roomTypes: filterDefaultValues.get(CompanyHotelDashboardFilter.ROOM)?.defaultValue,
					meal: filterDefaultValues.get(CompanyHotelDashboardFilter.MEAL)?.defaultValue,
					airportCode: filterDefaultValues.get(CompanyHotelDashboardFilter.AIRPORT_CODE)?.defaultValue,
					competitorTagIds: filterDefaultValues.get(CompanyHotelDashboardFilter.COMPETITOR_TAGS)?.defaultValue,
				};
				setFilters(newFilters);
			}
		}

		if (!!newRegion) {
			setFilters({ ...filters, region: newRegion });
		}
	}, [filters.provider, filters.region, companyHotel.settings.main.region]);


	function findProviderFiltersConfiguration(companyHotel: CompanyHotel, currentFilter: MarketStateFilterInput | undefined): Map<DataProvider | undefined, Map<CompanyHotelDashboardFilter, FilterConfig>> {
		const defaultRegion = companyHotel.settings.main.region;
		const defaultNights = companyHotel.settings?.defaultParsing?.nights?.[0] ?? 1;

		return new Map([
			[undefined, new Map<CompanyHotelDashboardFilter, FilterConfig>([
				[CompanyHotelDashboardFilter.PROVIDER, { enabled: true, defaultValue: undefined }],
				[CompanyHotelDashboardFilter.REGION, { enabled: true, defaultValue: defaultRegion }],
				[CompanyHotelDashboardFilter.GUESTS, { enabled: true, defaultValue: 2 }],
				[CompanyHotelDashboardFilter.NIGHTS, { enabled: true, defaultValue: defaultNights }],
				[CompanyHotelDashboardFilter.RATE, { enabled: false, defaultValue: RatesType.PUBLIC }],
				[CompanyHotelDashboardFilter.PLATFORM, { enabled: false, defaultValue: DataPlatform.WEB }],
				[CompanyHotelDashboardFilter.FLEX, { enabled: true, defaultValue: Refunding.NONE }],
				[CompanyHotelDashboardFilter.ROOM, { enabled: true, defaultValue: [] }],
				[CompanyHotelDashboardFilter.MEAL, { enabled: true, defaultValue: undefined }],
				[CompanyHotelDashboardFilter.AIRPORT_CODE, { enabled: false, defaultValue: undefined }],
				[CompanyHotelDashboardFilter.COMPETITOR_TAGS, { enabled: true, defaultValue: [] }],
			])],
			[DataProvider.BOOKING, new Map<CompanyHotelDashboardFilter, FilterConfig>([
				[CompanyHotelDashboardFilter.PROVIDER, { enabled: true, defaultValue: DataProvider.BOOKING }],
				[CompanyHotelDashboardFilter.REGION, { enabled: true, defaultValue: !!currentFilter ? currentFilter.region : defaultRegion }],
				[CompanyHotelDashboardFilter.GUESTS, { enabled: true, defaultValue: !!currentFilter ? currentFilter.guests : 2 }],
				[CompanyHotelDashboardFilter.NIGHTS, { enabled: true, defaultValue: !!currentFilter ? currentFilter.nights : defaultNights }],
				[CompanyHotelDashboardFilter.RATE, { enabled: true, defaultValue: !!currentFilter ? currentFilter.ratesType : RatesType.PUBLIC }],
				[CompanyHotelDashboardFilter.PLATFORM, { enabled: true, defaultValue: DataPlatform.WEB }],
				[CompanyHotelDashboardFilter.FLEX, { enabled: true, defaultValue: !!currentFilter ? currentFilter.refunding : Refunding.NONE }],
				[CompanyHotelDashboardFilter.ROOM, { enabled: true, defaultValue: !!currentFilter ? currentFilter.roomTypes : [] }],
				[CompanyHotelDashboardFilter.MEAL, { enabled: true, defaultValue: undefined }],
				[CompanyHotelDashboardFilter.AIRPORT_CODE, { enabled: false, defaultValue: undefined }],
				[CompanyHotelDashboardFilter.COMPETITOR_TAGS, { enabled: true, defaultValue: !!currentFilter ? currentFilter.competitorTagIds : [] }],
			])],
			[DataProvider.EXPEDIA, new Map<CompanyHotelDashboardFilter, FilterConfig>([
				[CompanyHotelDashboardFilter.PROVIDER, { enabled: true, defaultValue: DataProvider.EXPEDIA }],
				[CompanyHotelDashboardFilter.REGION, { enabled: false, defaultValue: defaultRegion }],
				[CompanyHotelDashboardFilter.GUESTS, { enabled: true, defaultValue: !!currentFilter ? currentFilter.guests : 2 }],
				[CompanyHotelDashboardFilter.NIGHTS, { enabled: true, defaultValue: !!currentFilter ? currentFilter.nights : defaultNights }],
				[CompanyHotelDashboardFilter.RATE, { enabled: false, defaultValue: RatesType.PUBLIC }],
				[CompanyHotelDashboardFilter.PLATFORM, { enabled: false, defaultValue: DataPlatform.WEB }],
				[CompanyHotelDashboardFilter.FLEX, { enabled: true, defaultValue: !!currentFilter ? currentFilter.refunding : Refunding.NONE }],
				[CompanyHotelDashboardFilter.ROOM, { enabled: true, defaultValue: !!currentFilter ? currentFilter.roomTypes : [] }],
				[CompanyHotelDashboardFilter.MEAL, { enabled: true, defaultValue: undefined }],
				[CompanyHotelDashboardFilter.AIRPORT_CODE, { enabled: false, defaultValue: undefined }],
				[CompanyHotelDashboardFilter.COMPETITOR_TAGS, { enabled: true, defaultValue: !!currentFilter ? currentFilter.competitorTagIds : [] }],
			])],
			[DataProvider.HOLIDAYCHECK, new Map<CompanyHotelDashboardFilter, FilterConfig>([
				[CompanyHotelDashboardFilter.PROVIDER, { enabled: true, defaultValue: DataProvider.HOLIDAYCHECK }],
				[CompanyHotelDashboardFilter.REGION, { enabled: false, defaultValue: !!currentFilter ? currentFilter.region : defaultRegion }],
				[CompanyHotelDashboardFilter.GUESTS, { enabled: true, defaultValue: !!currentFilter ? currentFilter.guests : 2 }],
				[CompanyHotelDashboardFilter.NIGHTS, { enabled: true, defaultValue: !!currentFilter ? currentFilter.nights : defaultNights }],
				[CompanyHotelDashboardFilter.RATE, { enabled: false, defaultValue: !!currentFilter ? currentFilter.ratesType : RatesType.PUBLIC }],
				[CompanyHotelDashboardFilter.PLATFORM, { enabled: false, defaultValue: DataPlatform.WEB }],
				[CompanyHotelDashboardFilter.FLEX, { enabled: true, defaultValue: !!currentFilter ? currentFilter.refunding : Refunding.NONE }],
				[CompanyHotelDashboardFilter.ROOM, { enabled: true, defaultValue: !!currentFilter ? currentFilter.roomTypes : [] }],
				[CompanyHotelDashboardFilter.MEAL, { enabled: true, defaultValue: undefined }],
				[CompanyHotelDashboardFilter.AIRPORT_CODE, { enabled: true, defaultValue: undefined }],
				[CompanyHotelDashboardFilter.COMPETITOR_TAGS, { enabled: true, defaultValue: !!currentFilter ? currentFilter.competitorTagIds : [] }],
			])],
			[DataProvider.BRAND_COM, new Map<CompanyHotelDashboardFilter, FilterConfig>([
				[CompanyHotelDashboardFilter.PROVIDER, { enabled: true, defaultValue: DataProvider.BRAND_COM }],
				[CompanyHotelDashboardFilter.REGION, { enabled: true, defaultValue: !!currentFilter ? currentFilter.region : defaultRegion }],
				[CompanyHotelDashboardFilter.GUESTS, { enabled: true, defaultValue: !!currentFilter ? currentFilter.guests : 2 }],
				[CompanyHotelDashboardFilter.NIGHTS, { enabled: true, defaultValue: !!currentFilter ? currentFilter.nights : defaultNights }],
				[CompanyHotelDashboardFilter.RATE, { enabled: true, defaultValue: !!currentFilter ? currentFilter.ratesType : RatesType.PUBLIC }],
				[CompanyHotelDashboardFilter.PLATFORM, { enabled: false, defaultValue: DataPlatform.WEB }],
				[CompanyHotelDashboardFilter.FLEX, { enabled: true, defaultValue: !!currentFilter ? currentFilter.refunding : Refunding.NONE }],
				[CompanyHotelDashboardFilter.ROOM, { enabled: true, defaultValue: !!currentFilter ? currentFilter.roomTypes : [] }],
				[CompanyHotelDashboardFilter.MEAL, { enabled: true, defaultValue: undefined }],
				[CompanyHotelDashboardFilter.AIRPORT_CODE, { enabled: false, defaultValue: undefined }],
				[CompanyHotelDashboardFilter.COMPETITOR_TAGS, { enabled: true, defaultValue: !!currentFilter ? currentFilter.competitorTagIds : [] }],
			])],
			[DataProvider.AGODA, new Map<CompanyHotelDashboardFilter, FilterConfig>([
				[CompanyHotelDashboardFilter.PROVIDER, { enabled: true, defaultValue: DataProvider.AGODA }],
				[CompanyHotelDashboardFilter.REGION, { enabled: false, defaultValue: !!currentFilter ? currentFilter.region : defaultRegion }],
				[CompanyHotelDashboardFilter.GUESTS, { enabled: true, defaultValue: !!currentFilter ? currentFilter.guests : 2 }],
				[CompanyHotelDashboardFilter.NIGHTS, { enabled: true, defaultValue: !!currentFilter ? currentFilter.nights : defaultNights }],
				[CompanyHotelDashboardFilter.RATE, { enabled: false, defaultValue: !!currentFilter ? currentFilter.ratesType : RatesType.PUBLIC }],
				[CompanyHotelDashboardFilter.PLATFORM, { enabled: false, defaultValue: DataPlatform.WEB }],
				[CompanyHotelDashboardFilter.FLEX, { enabled: true, defaultValue: !!currentFilter ? currentFilter.refunding : Refunding.NONE }],
				[CompanyHotelDashboardFilter.ROOM, { enabled: true, defaultValue: !!currentFilter ? currentFilter.roomTypes : [] }],
				[CompanyHotelDashboardFilter.MEAL, { enabled: true, defaultValue: undefined }],
				[CompanyHotelDashboardFilter.AIRPORT_CODE, { enabled: false, defaultValue: undefined }],
				[CompanyHotelDashboardFilter.COMPETITOR_TAGS, { enabled: true, defaultValue: !!currentFilter ? currentFilter.competitorTagIds : [] }],
			])],
			[DataProvider.AIRBNB, new Map<CompanyHotelDashboardFilter, FilterConfig>([
				[CompanyHotelDashboardFilter.PROVIDER, { enabled: true, defaultValue: DataProvider.AIRBNB }],
				[CompanyHotelDashboardFilter.REGION, { enabled: false, defaultValue: !!currentFilter ? currentFilter.region : defaultRegion }],
				[CompanyHotelDashboardFilter.GUESTS, { enabled: false, defaultValue: !!currentFilter ? currentFilter.guests : 2 }],
				[CompanyHotelDashboardFilter.NIGHTS, { enabled: true, defaultValue: !!currentFilter ? currentFilter.nights : defaultNights }],
				[CompanyHotelDashboardFilter.RATE, { enabled: false, defaultValue: !!currentFilter ? currentFilter.ratesType : RatesType.PUBLIC }],
				[CompanyHotelDashboardFilter.PLATFORM, { enabled: false, defaultValue: DataPlatform.WEB }],
				[CompanyHotelDashboardFilter.FLEX, { enabled: false, defaultValue: !!currentFilter ? currentFilter.refunding : Refunding.NONE }],
				[CompanyHotelDashboardFilter.ROOM, { enabled: false, defaultValue: !!currentFilter ? currentFilter.roomTypes : [] }],
				[CompanyHotelDashboardFilter.MEAL, { enabled: false, defaultValue: undefined }],
				[CompanyHotelDashboardFilter.AIRPORT_CODE, { enabled: false, defaultValue: undefined }],
				[CompanyHotelDashboardFilter.COMPETITOR_TAGS, { enabled: true, defaultValue: !!currentFilter ? currentFilter.competitorTagIds : [] }],
			])],
			[DataProvider.TRAVELSUPERMARKET, new Map<CompanyHotelDashboardFilter, FilterConfig>([
				[CompanyHotelDashboardFilter.PROVIDER, { enabled: true, defaultValue: DataProvider.TRAVELSUPERMARKET }],
				[CompanyHotelDashboardFilter.REGION, { enabled: false, defaultValue: !!currentFilter ? currentFilter.region : defaultRegion }],
				[CompanyHotelDashboardFilter.GUESTS, { enabled: true, defaultValue: !!currentFilter ? currentFilter.guests : 2 }],
				[CompanyHotelDashboardFilter.NIGHTS, { enabled: false, defaultValue: 7 }],
				[CompanyHotelDashboardFilter.RATE, { enabled: false, defaultValue: !!currentFilter ? currentFilter.ratesType : RatesType.PUBLIC }],
				[CompanyHotelDashboardFilter.PLATFORM, { enabled: false, defaultValue: DataPlatform.WEB }],
				[CompanyHotelDashboardFilter.FLEX, { enabled: true, defaultValue: !!currentFilter ? currentFilter.refunding : Refunding.NONE }],
				[CompanyHotelDashboardFilter.ROOM, { enabled: true, defaultValue: !!currentFilter ? currentFilter.roomTypes : [] }],
				[CompanyHotelDashboardFilter.MEAL, { enabled: true, defaultValue: undefined }],
				[CompanyHotelDashboardFilter.COMPETITOR_TAGS, { enabled: true, defaultValue: !!currentFilter ? currentFilter.competitorTagIds : [] }],
				[CompanyHotelDashboardFilter.AIRPORT_CODE, {enabled: true, defaultValue: undefined}],
				[CompanyHotelDashboardFilter.COMPETITOR_TAGS, { enabled: true, defaultValue: [] }],
			])],
			[DataProvider.TUIUK, new Map<CompanyHotelDashboardFilter, FilterConfig>([
				[CompanyHotelDashboardFilter.PROVIDER, { enabled: true, defaultValue: DataProvider.TUIUK }],
				[CompanyHotelDashboardFilter.REGION, { enabled: false, defaultValue: !!currentFilter ? currentFilter.region : defaultRegion }],
				[CompanyHotelDashboardFilter.GUESTS, { enabled: true, defaultValue: !!currentFilter ? currentFilter.guests : 2 }],
				[CompanyHotelDashboardFilter.NIGHTS, { enabled: true, defaultValue: 7 }],
				[CompanyHotelDashboardFilter.RATE, { enabled: false, defaultValue: !!currentFilter ? currentFilter.ratesType : RatesType.PUBLIC }],
				[CompanyHotelDashboardFilter.PLATFORM, { enabled: false, defaultValue: DataPlatform.WEB }],
				[CompanyHotelDashboardFilter.FLEX, { enabled: true, defaultValue: !!currentFilter ? currentFilter.refunding : Refunding.NONE }],
				[CompanyHotelDashboardFilter.ROOM, { enabled: true, defaultValue: !!currentFilter ? currentFilter.roomTypes : [] }],
				[CompanyHotelDashboardFilter.MEAL, { enabled: true, defaultValue: undefined }],
				[CompanyHotelDashboardFilter.COMPETITOR_TAGS, { enabled: true, defaultValue: !!currentFilter ? currentFilter.competitorTagIds : [] }],
				[CompanyHotelDashboardFilter.AIRPORT_CODE, {enabled: true, defaultValue: undefined}],
				[CompanyHotelDashboardFilter.COMPETITOR_TAGS, { enabled: true, defaultValue: [] }],
			])],
			[DataProvider.TUIDE, new Map<CompanyHotelDashboardFilter, FilterConfig>([
				[CompanyHotelDashboardFilter.PROVIDER, { enabled: true, defaultValue: DataProvider.TUIDE }],
				[CompanyHotelDashboardFilter.REGION, { enabled: false, defaultValue: !!currentFilter ? currentFilter.region : defaultRegion }],
				[CompanyHotelDashboardFilter.GUESTS, { enabled: true, defaultValue: !!currentFilter ? currentFilter.guests : 2 }],
				[CompanyHotelDashboardFilter.NIGHTS, { enabled: true, defaultValue: 7 }],
				[CompanyHotelDashboardFilter.RATE, { enabled: false, defaultValue: !!currentFilter ? currentFilter.ratesType : RatesType.PUBLIC }],
				[CompanyHotelDashboardFilter.PLATFORM, { enabled: false, defaultValue: DataPlatform.WEB }],
				[CompanyHotelDashboardFilter.FLEX, { enabled: true, defaultValue: !!currentFilter ? currentFilter.refunding : Refunding.NONE }],
				[CompanyHotelDashboardFilter.ROOM, { enabled: true, defaultValue: !!currentFilter ? currentFilter.roomTypes : [] }],
				[CompanyHotelDashboardFilter.MEAL, { enabled: true, defaultValue: undefined }],
				[CompanyHotelDashboardFilter.AIRPORT_CODE, { enabled: true, defaultValue: undefined }],
				[CompanyHotelDashboardFilter.COMPETITOR_TAGS, { enabled: true, defaultValue: !!currentFilter ? currentFilter.competitorTagIds : [] }],
			])],
			[DataProvider.JET2, new Map<CompanyHotelDashboardFilter, FilterConfig>([
				[CompanyHotelDashboardFilter.PROVIDER, { enabled: true, defaultValue: DataProvider.JET2 }],
				[CompanyHotelDashboardFilter.REGION, { enabled: false, defaultValue: !!currentFilter ? currentFilter.region : defaultRegion }],
				[CompanyHotelDashboardFilter.GUESTS, { enabled: true, defaultValue: !!currentFilter ? currentFilter.guests : 2 }],
				[CompanyHotelDashboardFilter.NIGHTS, { enabled: false, defaultValue: 7 }],
				[CompanyHotelDashboardFilter.RATE, { enabled: false, defaultValue: !!currentFilter ? currentFilter.ratesType : RatesType.PUBLIC }],
				[CompanyHotelDashboardFilter.PLATFORM, { enabled: false, defaultValue: DataPlatform.WEB }],
				[CompanyHotelDashboardFilter.FLEX, { enabled: true, defaultValue: !!currentFilter ? currentFilter.refunding : Refunding.NONE }],
				[CompanyHotelDashboardFilter.ROOM, { enabled: true, defaultValue: !!currentFilter ? currentFilter.roomTypes : [] }],
				[CompanyHotelDashboardFilter.MEAL, { enabled: true, defaultValue: undefined }],
				[CompanyHotelDashboardFilter.AIRPORT_CODE, { enabled: true, defaultValue: "LGW" as  AirportCodeJet2Key }],
				[CompanyHotelDashboardFilter.COMPETITOR_TAGS, { enabled: true, defaultValue: !!currentFilter ? currentFilter.competitorTagIds : [] }],
			])],
		]);
	}


	const isDashboardFilterEnabledForFilter = useCallback((f: CompanyHotelDashboardFilter, filters: MarketStateFilterInput) => {
		return findProviderFiltersConfiguration(companyHotel, filters).get(filters.provider!)?.get(f)?.enabled ?? false;
	}, [filters]);

	const isDashboardFilterEnabled = useCallback((f: CompanyHotelDashboardFilter) => {
		return isDashboardFilterEnabledForFilter(f, filters);
	}, [filters, isDashboardFilterEnabledForFilter]);

	const getFilterDefaultValue = useCallback((f: CompanyHotelDashboardFilter, provider: DataProvider) => {
		return findProviderFiltersConfiguration(companyHotel, filters).get(provider)?.get(f)?.defaultValue;
	}, [filters]);

	const resetFilters = useCallback(() => {
		_setFilters(defaultFilters);
	}, []);

	const setFilters = useCallback((filters: MarketStateFilterInput) => {
		_setFilters(filters);
	}, []);

	const getParityFiltersCallback = useCallback((nights: number) => {
		return getParityFilters(filters, nights);
	}, [filters]);


	const contextValue = useMemo(() => ({
		filter: filters,
		getParityFilters: getParityFiltersCallback,
		setFilters: setFilters,
		resetFilters: resetFilters,
		isDashboardFilterEnabled: isDashboardFilterEnabled,
	}), [filters, getParityFiltersCallback, setFilters, resetFilters, isDashboardFilterEnabled]);

	return <MarketStateFilterInputContext.Provider value={contextValue}>
		{children}
	</MarketStateFilterInputContext.Provider>;
};
